import type { InterviewDecision } from "@repo/app-types/entities/interview";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { getIsRecruitingOrTrainingProject } from "@repo/app-types/helpers/utilities";
import { Input } from "@repo/ui/Inputs/input";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { IconSearch } from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { AssessmentDistributionBar } from "./assessmentDistributionBar.js";
import { InterviewSlideoverContainer } from "./interviewSlideover/interviewSlideoverContainer.js";
import {
  fetchNonEmptyInterviewsForProject,
  type InterviewStatusFilter,
  selectInterviewsForCurrentParametersWithContactsAndActivities,
  selectInterviewsParameters,
} from "./interviewsSlice.js";
import { InterviewsTable } from "./interviewsTable.js";

const minAssessmentScoreSelectOptions: readonly SimpleSelectOption<
  number | null
>[] = [
  { label: "Any score", value: null },
  { label: "2 or higher", value: 2 },
  { label: "3 or higher", value: 3 },
  { label: "4 or higher", value: 4 },
  { label: "5", value: 5 },
];

const statusFilterOptions: SimpleSelectOption<InterviewStatusFilter>[] = [
  { label: "Interviews", value: "active" },
  { label: "Pending interviews", value: "pending" },
];

const decisionFilterOptions: SimpleSelectOption<
  InterviewDecision | "pending" | null
>[] = [
  { label: "All", value: null },
  { label: "Pending", value: "pending" },
  { label: "Advanced", value: "advanced" },
  { label: "Rejected", value: "rejected" },
];

interface ProjectInsights {
  assessment_distribution: Record<number, number>;
}

interface InterviewsTabProps {
  project: ProjectWithInterviewCount;
}

export const InterviewsTab: React.FC<InterviewsTabProps> = ({ project }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [minAssessmentScore, setMinAssessmentScore] = useState<number>();
  const [statusFilter, setStatusFilter] =
    useState<InterviewStatusFilter>("active");
  const [insights, setInsights] = useState<ProjectInsights | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);

  const interviews = useAppSelector(
    selectInterviewsForCurrentParametersWithContactsAndActivities,
  );

  const interviewsStatus = useAppSelector((state) => state.interviews.status);
  const interviewsParameters = useAppSelector(selectInterviewsParameters);
  const isSearchView = !!interviewsParameters?.search_text;
  const isRecruitingOrTrainingProject =
    getIsRecruitingOrTrainingProject(project);

  // Fetch interviews at least once when the component mounts or the project changes.
  useEffect(() => {
    dispatch(
      fetchNonEmptyInterviewsForProject({
        projectId: project.id,
        page: 1,
      }),
    );
  }, [project.id]);

  useEffect(() => {
    const fetchInsights = async (): Promise<void> => {
      try {
        const axios = await getAxiosInstanceWithAuth();
        const response = await axios.get(`/projects/${project.id}/insights`);
        setInsights(response.data as ProjectInsights);
      } catch (error) {
        console.error("Failed to fetch insights:", error);
      } finally {
        setIsLoadingInsights(false);
      }
    };

    if (isRecruitingOrTrainingProject) {
      void fetchInsights();
    } else {
      setIsLoadingInsights(false);
    }

    return () => {
      setInsights(null);
    };
  }, [project.id, isRecruitingOrTrainingProject]);

  const onSearchTextChange = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
    setMinAssessmentScore(undefined);
    dispatch(
      fetchNonEmptyInterviewsForProject({
        projectId: project.id,
        page: 1,
        search_text: newSearchQuery,
        min_assessment_score: undefined,
        status_filter: undefined,
      }),
    );
  };

  const onMinAssessmentScoreChange = (newMinAssessmentScore?: number) => {
    setSearchQuery("");
    setMinAssessmentScore(newMinAssessmentScore);
    setStatusFilter("active");
    dispatch(
      fetchNonEmptyInterviewsForProject({
        projectId: project.id,
        page: 1,
        search_text: "",
        min_assessment_score: newMinAssessmentScore,
        status_filter: "active",
        decision_filter: interviewsParameters?.decision_filter,
      }),
    );
  };

  const onStatusFilterChange = (newStatusFilter: InterviewStatusFilter) => {
    setSearchQuery("");
    setMinAssessmentScore(undefined);
    setStatusFilter(newStatusFilter);
    void dispatch(
      fetchNonEmptyInterviewsForProject({
        projectId: project.id,
        page: 1,
        search_text: "",
        min_assessment_score: undefined,
        status_filter: newStatusFilter,
        decision_filter: undefined,
      }),
    );
  };

  const onDecisionFilterChange = (
    value: InterviewDecision | "pending" | null,
  ) => {
    dispatch(
      fetchNonEmptyInterviewsForProject({
        projectId: project.id,
        page: 1,
        search_text: "",
        min_assessment_score: minAssessmentScore,
        status_filter: statusFilter,
        decision_filter: value ?? undefined,
      }),
    );
  };

  const renderFilterControls = () => {
    // No filters for non-recruiting projects or when searching.
    if (!isRecruitingOrTrainingProject || isSearchView) {
      return null;
    }

    return (
      <>
        <SimpleSelect
          label="Interview status"
          isLabelHidden
          value={statusFilter}
          onChange={onStatusFilterChange}
          options={statusFilterOptions}
        />
        {statusFilter === "active" && (
          <div className="grid w-[150px]">
            <SimpleSelect
              label="Minimum score"
              isLabelHidden
              value={minAssessmentScore ?? null}
              onChange={(value) => {
                onMinAssessmentScoreChange(value ?? undefined);
              }}
              options={minAssessmentScoreSelectOptions}
              placeholder="Minimum score"
            />
          </div>
        )}
        {statusFilter === "active" && (
          <div className="w-[120px]">
            <SimpleSelect
              label="Interview decision"
              isLabelHidden
              value={interviewsParameters?.decision_filter ?? null}
              onChange={onDecisionFilterChange}
              options={decisionFilterOptions}
              placeholder="Decision"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col pt-4">
      {isRecruitingOrTrainingProject ? (
        <AssessmentDistributionBar
          onScoreClick={(score) => {
            if (score === minAssessmentScore) {
              onMinAssessmentScoreChange(undefined);
            } else {
              onMinAssessmentScoreChange(score);
            }
          }}
          scoreDistribution={insights?.assessment_distribution ?? null}
          isLoading={isLoadingInsights}
        />
      ) : null}
      <div className="mb-4 flex flex-row justify-between">
        <div className="flex flex-row gap-2">
          <div className="w-[260px]">
            <Input
              leftIcon={<IconSearch className="h-4 w-4 text-gray-700" />}
              maxLength={200}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              onClear={() => {
                onSearchTextChange("");
              }}
              onKeyDown={(evt) => {
                if (evt.key === "Enter") {
                  evt.preventDefault();
                  onSearchTextChange(searchQuery);
                  (evt.target as HTMLElement).blur();
                }
              }}
              placeholder="Search transcripts and contacts..."
              value={searchQuery}
            />
          </div>

          {renderFilterControls()}
        </div>
      </div>
      <InterviewsTable
        interviews={interviews}
        isLoading={interviewsStatus === "loading" || isLoadingInsights}
        onInterviewClick={(
          interviewId: string,
          transcriptFragmentId?: string,
        ) => {
          navigate(
            buildProjectUrlPath(
              project.id,
              ProjectDetailsTabPath.Interviews,
              interviewId,
              transcriptFragmentId,
            ),
          );
        }}
        project={project}
      />

      <InterviewSlideoverContainer project={project} />
    </div>
  );
};
